import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TextField } from '@react-md/form';
import { FeatureContent } from '@components/feature-content';
import { FormField, NonFieldErrors } from '@components/form-field';
import { SubmitButton } from '@components/submit-button';
import type { FetchBaseQueryError } from '@services/df/baseApi';
import { useUserFormLabels } from '@modules/user';
import { setApiErrors } from '@utils';
import { useValidation } from '@hooks';
import { createLoginOptionsValidation } from '../form-validation';
import { signInId } from '../helpers';
import { SignInOptionsForm } from '../types';
import { SignInOptionsFormId } from '../constants';

interface UseSignInOptionsFormProps {
    email?: string;
    request: (data: SignInOptionsForm) => Promise<void>;
    onSuccess?: () => void;
}

const useSignInOptionsForm = ({ email, request, onSuccess }: UseSignInOptionsFormProps) => {
    const validationSchema = useValidation(createLoginOptionsValidation);
    const formApi = useForm<SignInOptionsForm>({
        defaultValues: {
            email: email ?? '',
        },
        resolver: yupResolver(validationSchema),
    });

    const {
        setError,
        formState: { isSubmitting: isSignInOptionsLoading },
    } = formApi;

    const submit: SubmitHandler<SignInOptionsForm> = async form => {
        try {
            const response = await request(form);

            onSuccess?.();
        } catch (error) {
            const errors = (error as FetchBaseQueryError).data;
            setApiErrors(setError, errors);
        }
    };
    return {
        formApi,
        isSignInOptionsLoading,
        submit,
    };
};

interface Props extends UseSignInOptionsFormProps {
    email?: string;
}

export const AuthSignInOptionsForm: React.FC<Props> = ({ children, email, request, onSuccess }) => {
    const { formApi, isSignInOptionsLoading, submit } = useSignInOptionsForm({ email, request, onSuccess });

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = formApi;

    const { t } = useTranslation();
    const labels = useUserFormLabels(t);

    return (
        <>
            <form
                id={SignInOptionsFormId}
                className={cn('fieldset-grid-cell', 'fieldset-grid')}
                onSubmit={handleSubmit(submit)}
            >
                <FormField
                    prefix={SignInOptionsFormId}
                    name="email"
                    control={control}
                    component={TextField}
                    label={labels.email}
                />

                <NonFieldErrors errors={errors} />
            </form>

            <div className={cn('emulated-flex-gap')}>
                <SubmitButton prefix={signInId()} form={SignInOptionsFormId} isSending={isSignInOptionsLoading}>
                    <FeatureContent contentKey={signInId('submit')} fallback={t('buttons.nextStep', 'Next step')} />
                </SubmitButton>
            </div>
        </>
    );
};
